import { normalizeBidPriceType } from './upload-validator';

/**
 * Submit All eligibility logic for bond bidding.
 * Shared by bond-bidding route (Submit All button) and column config (per-row Submit).
 * Must stay in sync with backend rule: new bids or status INSERT_ACCEPTED/AMEND_ACCEPTED/REJECTED.
 */

/** Bid statuses that allow (re-)submit; must match backend rule. */
export const SUBMIT_ALLOWED_BID_STATUSES = ['INSERT_ACCEPTED', 'AMEND_ACCEPTED', 'REJECTED'] as const;

/** Normalize BID_STATUS for comparison (e.g. "Insert Accepted" → "INSERT_ACCEPTED"). */
export function normalizeBidStatus(s: string): string {
  return (s ?? '').toUpperCase().replace(/\s+/g, '_');
}

/** True if status allows submit: no status (new bid) or INSERT_ACCEPTED/AMEND_ACCEPTED/REJECTED. */
export function isSubmittableBidStatus(status: string): boolean {
  const n = normalizeBidStatus(status);
  if (!n) return true;
  return SUBMIT_ALLOWED_BID_STATUSES.includes(n as (typeof SUBMIT_ALLOWED_BID_STATUSES)[number]);
}

/** Row-like data used for eligibility checks. */
export interface SubmitEligibilityRow {
  BOND_ID?: string | null;
  BID_ID?: string | null;
  BID_PRICE?: string | number | null;
  BID_PRICE_TYPE?: string | null;
  BID_STATUS?: string | null;
  STATUS?: string | null;
  DUE_TIME?: string | number | Date | null;
  _originalBidPrice?: string | number | null;
  _originalBidPriceType?: string | null;
}

/** True if row has pending changes (upload/edit) that warrant showing Submit. */
export function hasRowPendingChanges(data: SubmitEligibilityRow): boolean {
  const hasPriceChange =
    data._originalBidPrice != null &&
    String(data._originalBidPrice) !== String(data.BID_PRICE);

  const origTypeNorm =
    data._originalBidPriceType != null
      ? normalizeBidPriceType(String(data._originalBidPriceType))
      : '';
  const currTypeNorm = normalizeBidPriceType(
    (data.BID_PRICE_TYPE as string) ?? ''
  );
  const hasTypeChange =
    data._originalBidPriceType != null && origTypeNorm !== currTypeNorm;

  return (
    hasPriceChange ||
    hasTypeChange
  );
}

export interface EligibleBidToSubmit {
  BOND_ID: string;
  BID_PRICE: string;
  BID_PRICE_TYPE: string;
}

/**
 * Returns rows eligible for Submit All from a list of row data.
 * Same rules as backend: new bids or INSERT_ACCEPTED/AMEND_ACCEPTED/REJECTED; for existing bids, only if has pending changes; valid response and due time.
 */
export function getEligibleRowsForSubmitAll(
  rows: SubmitEligibilityRow[],
  referenceIdSet: Set<string>,
  now: number = Date.now()
): EligibleBidToSubmit[] {
  const result: EligibleBidToSubmit[] = [];

  for (const data of rows) {
    const bondId = String(data.BOND_ID ?? '').trim();
    if (!bondId || !referenceIdSet.has(bondId)) continue;
    if (data.STATUS != null && data.STATUS !== 'DUE_IN') continue;

    const statusRaw = (data.BID_STATUS as string) ?? '';
    const statusNorm = normalizeBidStatus(statusRaw);
    const hasExistingBid = data.BID_ID != null && data.BID_ID !== '' && statusNorm;
    const allowedStatus = SUBMIT_ALLOWED_BID_STATUSES.includes(statusNorm as (typeof SUBMIT_ALLOWED_BID_STATUSES)[number]);

    if (hasExistingBid && !allowedStatus) continue;
    if (hasExistingBid && !hasRowPendingChanges(data)) continue;

    if (data.BID_PRICE == null || data.BID_PRICE === '') continue;
    if (!data.DUE_TIME) continue;
    const dueTime = new Date(data.DUE_TIME).getTime();
    if (now > dueTime) continue;

    result.push({
      BOND_ID: bondId,
      BID_PRICE: String(data.BID_PRICE),
      BID_PRICE_TYPE: normalizeBidPriceType(data.BID_PRICE_TYPE as string),
    });
  }

  return result;
}

